import { useLocalizedPath } from "../i18n/useLocalizedPath";
import React, { useEffect, useLayoutEffect, useRef } from "react";
import {
  Box,
  Flex,
  IconButton,
  Text,
  VStack,
  HStack,
  Drawer,
  DrawerOverlay,
  DrawerContent,
  DrawerBody,
  useDisclosure,
} from "@chakra-ui/react";
import { CloseIcon, HamburgerIcon } from "@chakra-ui/icons";
import logoImage from "../deepsoul_logo2.png";
import LanguageSelector from "./LanguageSelector";
import { useTranslation } from "react-i18next";
import { Link as RouterLink, useLocation } from "react-router-dom"; // 引入 useLocation

const Header: React.FC = () => {
  const { isOpen, onOpen, onClose } = useDisclosure();
  const menuButtonRef = useRef<HTMLButtonElement>(null);
  const { t } = useTranslation();
  const localizePath = useLocalizedPath();
  const location = useLocation(); // 使用 useLocation 監聽路由變化

  const HEADER_MENU = [
    { label: t("header.home"), path: "/" },
    { label: t("header.about"), path: "/about" },
    { label: t("footer.diary"), path: "/diary-app" },

    { label: t("header.tutorial"), path: "/tutorial" },
    { label: t("header.introVideo"), path: "/introVideo" },
    { label: t("header.news"), path: "/news" },
    { label: t("header.explore"), path: "/explore" },
    { label: t("header.hexaco"), path: "/hexaco" },
    //{ label: t("header.souli"), path: "/souli" },
  ];

  useEffect(() => {
    // 當路由變化時，捲動到頁面頂部
    window.scrollTo(0, 0);
  }, [location]); // 依賴於 location，當路由變化時觸發

  useLayoutEffect(() => {
    onClose();
  }, [
    location.hash,
    location.key,
    location.pathname,
    location.search,
    onClose,
  ]);

  return (
    <Box
      as="header"
      py={4}
      px={6}
      position="sticky"
      top={0}
      zIndex="sticky"
      isolation="isolate"
      backdropFilter="blur(10px)"
      bg="rgba(255, 255, 255, 0.7)"
    >
      <Flex align="center" justify="space-between" maxW="1100px" mx="auto">
        <Box
          display="flex"
          alignItems="center"
          gap={2}
          cursor="pointer"
          as={RouterLink}
          to={localizePath("/")}
          _hover={{ opacity: 0.8 }}
        >
          <Box
            w="40px"
            h="40px"
            bg="white"
            borderRadius="full"
            overflow="hidden"
          >
            <img
              src={logoImage}
              alt="DeepSoul Logo"
              style={{ width: "100%", height: "100%", objectFit: "cover" }}
            />
          </Box>
          <Text
            fontSize="lg"
            fontWeight="bold"
            color="primary.600"
            letterSpacing="tight"
          >
            Deep Soul
          </Text>
        </Box>
        <HStack spacing={6} display={{ base: "none", xl: "flex" }}>
          {HEADER_MENU.map((item) => (
            <Box
              key={item.label}
              as={RouterLink}
              fontSize="sm"
              fontWeight="medium"
              color="gray.700"
              _hover={{ color: "primary.600" }}
              to={localizePath(item.path)}
            >
              {item.label}
            </Box>
          ))}
          <LanguageSelector />
        </HStack>
        <Box display={{ base: "block", xl: "none" }}>
          <IconButton
            ref={menuButtonRef}
            data-testid="mobile-menu-button"
            aria-label={t("header.openMenu")}
            aria-expanded={isOpen}
            aria-controls="mobile-navigation-drawer"
            icon={<HamburgerIcon boxSize={6} />}
            minW="44px"
            minH="44px"
            w="44px"
            h="44px"
            variant="ghost"
            color="gray.800"
            sx={{ touchAction: "manipulation" }}
            onClick={onOpen}
            _focusVisible={{
              outline: "3px solid",
              outlineColor: "primary.300",
              outlineOffset: "2px",
            }}
          />
        </Box>
      </Flex>
      <Drawer
        placement="right"
        onClose={onClose}
        isOpen={isOpen}
        size="xs"
        finalFocusRef={menuButtonRef}
      >
        <DrawerOverlay motionProps={{ transition: { duration: 0 } }} />
        <DrawerContent
          id="mobile-navigation-drawer"
          aria-label={t("header.mobileNavigation")}
          motionProps={{ transition: { duration: 0 } }}
        >
          <DrawerBody p={0}>
            <Flex align="center" justify="space-between" px={4} py={4}>
              <Box display="flex" alignItems="center" gap={2}>
                <Box
                  w="40px"
                  h="40px"
                  bg="white"
                  borderRadius="full"
                  overflow="hidden"
                  as={RouterLink}
                  to={localizePath("/")}
                  onClick={onClose}
                  cursor="pointer"
                >
                  <img
                    src={logoImage}
                    alt="DeepSoul Logo"
                    style={{
                      width: "100%",
                      height: "100%",
                      objectFit: "cover",
                    }}
                  />
                </Box>
                <Text fontSize="md" fontWeight="bold" color="primary.600">
                  DeepSoul
                </Text>
              </Box>
              <IconButton
                aria-label={t("header.closeMenu")}
                icon={<CloseIcon boxSize={4} />}
                minW="44px"
                minH="44px"
                w="44px"
                h="44px"
                variant="ghost"
                sx={{ touchAction: "manipulation" }}
                onClick={onClose}
                _focusVisible={{
                  outline: "3px solid",
                  outlineColor: "primary.300",
                  outlineOffset: "2px",
                }}
              />
            </Flex>
            <VStack align="stretch" spacing={4} px={6} pt={4} pb={6}>
              {HEADER_MENU.map((item) => (
                <Box
                  key={item.label}
                  as={RouterLink}
                  textAlign="left"
                  py={2}
                  _hover={{ color: "primary.600" }}
                  to={localizePath(item.path)}
                  onClick={onClose}
                >
                  <Text fontWeight="medium">{item.label}</Text>
                </Box>
              ))}
              <Box py={2}>
                <LanguageSelector />
              </Box>
            </VStack>
          </DrawerBody>
        </DrawerContent>
      </Drawer>
    </Box>
  );
};

export default Header;

